import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { User, Package, MapPin, LogOut, FileText, MessageCircle } from "lucide-react";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

interface AccountMenuProps {
  onReorderClick?: () => void;
}

const AccountMenu = ({ onReorderClick }: AccountMenuProps) => {
  const { user, loading, signOut } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [userName, setUserName] = useState<string | null>(null);
  const [whatsappNumber, setWhatsappNumber] = useState<string>("+971504996672");
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setUserName(null);
      setActiveOrdersCount(0);
    }
  }, [user]);

  useEffect(() => {
    // Fetch WhatsApp number from contact details
    const fetchWhatsApp = async () => {
      const { data } = await supabase
        .from("contact_details")
        .select("whatsapp")
        .limit(1)
        .maybeSingle();
      if (data?.whatsapp) {
        setWhatsappNumber(data.whatsapp);
      }
    };
    fetchWhatsApp();
  }, []);

  const fetchUserData = async () => {
    if (!user) return;

    // Fetch user profile for name
    const { data: profile } = await supabase
      .from("user_profiles")
      .select("full_name")
      .eq("user_id", user.id)
      .maybeSingle();

    if (profile?.full_name) {
      // Get first name
      const firstName = profile.full_name.split(" ")[0];
      setUserName(firstName);
    } else {
      // Use email prefix as fallback
      const emailPrefix = user.email?.split("@")[0] || null;
      setUserName(emailPrefix);
    }

    // Fetch active orders count (orders not delivered or cancelled)
    const { count } = await supabase
      .from("orders")
      .select("*", { count: "exact", head: true })
      .eq("customer_id", user.id)
      .not("status", "in", '("delivered","cancelled","rejected")');

    setActiveOrdersCount(count || 0);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const formatWhatsAppLink = (number: string) => {
    const cleanNumber = number.replace(/[^\d+]/g, "").replace("+", "");
    const isMobile =
      typeof navigator !== "undefined" &&
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    return isMobile
      ? `https://wa.me/${cleanNumber}`
      : `https://web.whatsapp.com/send?phone=${cleanNumber}`;
  };

  if (loading) {
    return (
        <Button variant="ghost" size="icon" className="h-11 w-11 [&_svg]:size-7" disabled>
          <User className="h-7 w-7" />
        <span className="hidden sm:inline">...</span>
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-11 w-11 [&_svg]:size-7 hover:bg-gold/10" aria-label={t("account.menu")}>
          <User className="h-7 w-7" />
          {activeOrdersCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-gold text-gold-foreground text-xs font-semibold h-5 min-w-5 px-1 rounded-full flex items-center justify-center">
              {activeOrdersCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-white border-amber-800/30 z-50">
        {user ? (
          <>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none text-black">{userName || t("account.customer")}</p>
                <p className="text-xs leading-none text-black/60 truncate text-left" dir="ltr">
                  {user.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            
            <DropdownMenuItem asChild className="text-black hover:bg-amber-50 focus:bg-amber-50">
              <Link to="/account" className="cursor-pointer">
                <User className="h-4 w-4 me-2" />
                {t("account.myProfile")}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="text-black hover:bg-amber-50 focus:bg-amber-50">
              <Link to="/account?tab=orders" className="cursor-pointer">
                <Package className="h-4 w-4 me-2" />
                {t("account.myOrders")}
                {activeOrdersCount > 0 && (
                  <span className="ms-auto bg-gold/20 text-gold text-xs px-2 py-0.5 rounded-full">
                    {activeOrdersCount} {t("account.active")}
                  </span>
                )}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="text-black hover:bg-amber-50 focus:bg-amber-50">
              <Link to="/account?tab=addresses" className="cursor-pointer">
                <MapPin className="h-4 w-4 me-2" />
                {t("account.myAddresses")}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuItem asChild className="text-black hover:bg-amber-50 focus:bg-amber-50">
              <Link to="/account?tab=invoices" className="cursor-pointer">
                <FileText className="h-4 w-4 me-2" />
                {t("account.invoices")}
              </Link>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => window.open(formatWhatsAppLink(whatsappNumber), "_blank")}
              className="cursor-pointer text-[#25D366] hover:bg-amber-50 focus:bg-amber-50"
            >
              <WhatsAppIcon className="h-4 w-4 me-2" />
              {t("account.contactAnaya")}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={handleSignOut}
              className="cursor-pointer text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4 me-2" />
              {t("btn.signOut")}
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuLabel className="text-black">{t("account.welcomeGuest")}</DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem asChild className="text-black hover:bg-amber-50 focus:bg-amber-50">
              <Link to="/auth" className="cursor-pointer">
                <User className="h-4 w-4 me-2" />
                {t("account.signInRegister")}
              </Link>
            </DropdownMenuItem>

            {onReorderClick && (
              <DropdownMenuItem onClick={onReorderClick} className="cursor-pointer text-black hover:bg-amber-50 focus:bg-amber-50">
                <Package className="h-4 w-4 me-2" />
                {t("account.reorderByCode")}
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem 
              onClick={() => window.open(formatWhatsAppLink(whatsappNumber), "_blank")}
              className="cursor-pointer text-[#25D366] hover:bg-amber-50 focus:bg-amber-50"
            >
              <WhatsAppIcon className="h-4 w-4 me-2" />
              {t("account.contactAnaya")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccountMenu;
